// feature/reparto-followup — generic insumos queue view. Same table for all 4 sub-routes
// (Solicitados / Recibidos / Adjudicados / Devueltos). Mode controls the
// filter applied + heading text.
import React, { useEffect, useState } from "react";
import { cases as casesApi, users as usersApi, insumos as insumosApi } from "../../api/endpoints";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import { TipoPill, EstadoPill } from "../../components/Pills";
import CaseDetail from "../case-detail";

function daysWaiting(c) {
  return Math.floor((Date.now() - new Date(c.fecha_ingreso).getTime()) / 86400000);
}

const MODE_CONFIG = {
  solicitados: {
    title: "Insumos solicitados",
    subtitle: "Casos pendientes de generar o enviar la solicitud de insumos.",
    estados: ["pendiente_insumos"],
    emptyMsg: "No hay solicitudes pendientes en este momento.",
  },
  recibidos: {
    title: "Insumos recibidos",
    subtitle: "Respuesta cargada — esperando que el abogado valide.",
    estados: ["insumos_recibidos"],
    extra: (c) => c.insumos_validos == null,
    emptyMsg: "No hay insumos recibidos sin validar.",
  },
  adjudicados: {
    title: "Insumos adjudicados",
    subtitle: "Casos que ya están en manos del abogado, trabajando con el insumo.",
    estados: ["en_elaboracion"],
    extra: (c) => !!c.requiere_insumo,
    emptyMsg: "No hay casos en elaboración con insumo.",
  },
  devueltos: {
    title: "Insumos devueltos",
    subtitle: "Devueltos por el abogado como insumos inválidos. Requieren seguimiento.",
    estados: ["devuelto_a_reparto"],
    emptyMsg: "No hay devoluciones pendientes.",
  },
};

export default function InsumosQueueView({ mode }) {
  const cfg = MODE_CONFIG[mode] || MODE_CONFIG.solicitados;
  const [data, setData] = useState(null);
  const [usersMap, setUsersMap] = useState({});
  const [openId, setOpenId] = useState(null);
  // sprint83 — cap status fed by /api/insumos/cap-status; surfaces a
  // small banner when any capped user is at/over the cap. Only loaded
  // in solicitados mode (the dispatch surface). Polled every 60s along
  // with the queue.
  const [capStatus, setCapStatus] = useState([]);

  function load() {
    setData(null);
    Promise.all(cfg.estados.map(e => casesApi.list({ estado: e })))
      .then(results => {
        let rows = results.flatMap(r => r.data || []);
        if (cfg.extra) rows = rows.filter(cfg.extra);
        rows.sort((a, b) => new Date(a.fecha_ingreso) - new Date(b.fecha_ingreso));
        setData(rows);
      })
      .catch(() => setData([]));
  }
  function loadCapStatus() {
    insumosApi.capStatus()
      .then((r) => setCapStatus(r.data.items || []))
      .catch(() => setCapStatus([]));
  }

  useEffect(() => {
    load();
    usersApi.list().then(r => {
      const m = {};
      for (const u of r.data) m[u.id] = u;
      setUsersMap(m);
    });
    if (mode === "solicitados") {
      loadCapStatus();
      const t = setInterval(loadCapStatus, 60_000);
      return () => clearInterval(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const total = data?.length ?? 0;

  // sprint83 — banner for capped users at/over their daily cap. First
  // hit only is rendered (currently only Natali has a cap, but if more
  // get added in lib/insumos_cap.js the loop will surface each).
  const atCap = (capStatus || []).filter((s) => s.at_cap);

  return (
    <div className="col" style={{ gap: 16 }}>
      <div>
        <h1 style={{ margin: 0 }}>{cfg.title}</h1>
        <div className="muted" style={{ marginTop: 4 }}>{cfg.subtitle}</div>
      </div>

      {mode === "solicitados" && atCap.map((s) => (
        <div key={s.user_id} className="card tight" style={{
          padding: 12, borderLeft: "3px solid var(--color-warning)",
          background: "var(--color-warning-bg)", color: "var(--color-warning-ink)",
        }}>
          <strong>{s.name.split(" ")[0]} alcanzó su cupo diario ({s.today}/{s.cap}).</strong>
          <div style={{ fontSize: 12, marginTop: 2 }}>
            Las asignaciones nuevas se redirigen automáticamente al miembro del equipo de insumos con menos asignaciones hoy.
          </div>
        </div>
      ))}

      <div className="card" style={{ padding: 0 }}>
        {data == null ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
        ) : !total ? (
          <Empty title="Cola vacía" message={cfg.emptyMsg} />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Caso</th>
                <th>Tipo de actuación</th>
                <th>Accionante</th>
                <th>Radicado</th>
                <th>Asignado a</th>
                <th>Días en trámite</th>
                <th>Estado de la actuación</th>
              </tr>
            </thead>
            <tbody>
              {data.map(c => {
                const assignee = c.assigned_insumos_id
                  ? usersMap[c.assigned_insumos_id]?.name || `#${c.assigned_insumos_id}`
                  : "—";
                return (
                  <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => setOpenId(c.id)}>
                    <td><strong>{c.case_number}</strong></td>
                    <td><TipoPill tipo={c.tipo} /></td>
                    <td>{c.accionante_nombre || "—"}</td>
                    <td>{c.radicado}</td>
                    <td>{assignee}</td>
                    <td>{daysWaiting(c)}</td>
                    <td><EstadoPill c={c} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <CaseDetail caseId={openId} open={!!openId} defaultTab="insumos" onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
